/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogContent,
  InputAdornment,
  Stack,
} from "@mui/material";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PropTypes from "prop-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { getUpkeepLocationNameQuery, getSiteSettingsMutation } from "../../lib/Queries";
import { useMutation } from "@tanstack/react-query";

const ManageUpkeepModal = ({ open, onClose, currentSite, organizationUuid }) => {
  const [locationId, setLocationId] = useState("");
  const [linkedId, setLinkedId] = useState("");
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const savedLocationId = currentSite?.upkeep_location_id || "";
  const isLinked = Boolean(linkedId);

  useEffect(() => {
    if (open) {
      setLocationId(savedLocationId);
      setLinkedId(savedLocationId);
    }
  }, [open, savedLocationId]);

  const {
    data: locationData,
    isFetching: isLocationLoading,
    isError: isLocationError,
  } = useQuery({
    ...getUpkeepLocationNameQuery(linkedId),
    enabled: Boolean(linkedId) && open,
  });

  const locationName = locationData?.name || "";

  const saveMutation = useMutation({
    ...getSiteSettingsMutation(),
    onSuccess: async () => {
      enqueueSnackbar("UpKeep integration saved.", {
        autoHideDuration: 3000,
        variant: "success",
      });
      queryClient.removeQueries({ queryKey: ["upkeep-location"] });
      const promises = [];
      if (organizationUuid) {
        promises.push(
          queryClient.refetchQueries({
            queryKey: ["organization-sites-list-" + organizationUuid],
          }),
        );
      }
      if (currentSite?.uuid) {
        promises.push(
          queryClient.refetchQueries({
            queryKey: ["site-metadata-" + currentSite.uuid],
          }),
        );
      }
      await Promise.all(promises);
      onClose();
    },
    onError: () => {
      enqueueSnackbar("Failed to save UpKeep integration.", {
        autoHideDuration: 4000,
        variant: "error",
      });
    },
  });

  const handleLink = () => {
    const trimmed = locationId.trim();
    if (!trimmed) return;
    setLinkedId(trimmed);
  };

  const handleSave = () => {
    if (!currentSite?.uuid || !linkedId) return;
    saveMutation.mutate({
      uuid: currentSite.uuid,
      upkeep_location_id: linkedId,
    });
  };

  const isSaving = saveMutation.isLoading;
  const hasChanges = linkedId !== savedLocationId;
  const canSave = isLinked && !isLocationLoading && !isLocationError && hasChanges;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}>
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Manage Integration
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            UpKeep
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              label="Location ID"
              size="small"
              variant="outlined"
              sx={{ width: 257 }}
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <LinkOutlinedIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              color="secondary"
              disabled={!locationId.trim() || isLocationLoading}
              onClick={handleLink}
            >
              {isLinked ? "Update" : "Link"}
            </Button>
          </Box>

          <Box
            sx={{
              bgcolor: "grey.100",
              borderRadius: 1,
              p: 3,
            }}
          >
            {isLinked && locationName ? (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Location
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {locationName}
                </Typography>
              </Box>
            ) : isLinked && isLocationLoading ? (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Loading location...
              </Typography>
            ) : isLinked && isLocationError ? (
              <Typography variant="caption" sx={{ color: "error.main" }}>
                Location ID not found in UpKeep.
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Enter an UpKeep location ID to associate it with this site.
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ color: "text.secondary" }}>
          <Typography variant="body2">{"To find a site\u2019s Location ID in UpKeep:"}</Typography>
          <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
            <Typography component="li" variant="body2">
              {
                "View the site in UpKeep and copy the Location ID from the web address. (\u201C.../location/"
              }
              <em>ienTbnNCT7</em>
              {"/...\u201D)"}
            </Typography>
            <Typography component="li" variant="body2">
              {"Or, go to "}
              <em>UpKeep &ndash; Locations &ndash; Download All</em>
              {", and identify the desired site from the downloaded file."}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!canSave || isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

ManageUpkeepModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentSite: PropTypes.object,
  organizationUuid: PropTypes.string,
};

export default ManageUpkeepModal;
